import { withCartSource } from '@/lib/cart-source';
import { createLogger } from '@/lib/logger';
import type { AddToCartPayload, Cart, ChangeLinePayload, UpdateCartPayload } from './core';
import { CartEvents } from './core';
import { CartEventBus } from './event-bus';

const jsonHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json; charset=UTF-8',
  // Add a custom header to the requests coming from this service so events aren't emitted twice
  'X-Source': 'mini-cart',
};

const cartRoute = (path: string) => `${window.Shopify.routes.root}${path}`;

/**
 * Service wrapper around Shopify Ajax Cart endpoints.
 *
 * - Emits lifecycle events via `CartEventBus` (see `CartEvents`).
 * - Sends `X-Source: mini-cart` on all requests to prevent duplicate event emission
 *   from the fetch interceptor when this service initiates the action.
 */
export class CartService {
  private bus: CartEventBus;
  private logger = createLogger('cart-service');

  /** Tail of the serial mutation queue. See `runExclusive`. */
  private tail: Promise<unknown> = Promise.resolve();

  /**
   * Create a CartService bound to a specific event bus instance.
   * @param bus Event bus used to emit lifecycle events.
   */
  constructor(bus: CartEventBus) {
    this.bus = bus;
  }

  /**
   * Run `task` after every previously queued task has settled.
   *
   * Shopify derives a line item key from the hash of its properties, so any
   * property write mints a new key. Without serialisation, a read-modify-write
   * (`GET /cart.js` → `POST /cart/change.js`) can have its key invalidated
   * mid-flight by a concurrent write, and Shopify answers the second request
   * with `400 no valid id or line parameter`.
   *
   * Tasks MUST only call the raw primitives on this service. Calling a
   * `SimpleCartService` method from inside a task re-enters the queue and
   * deadlocks.
   *
   * @param task Work to run while holding the queue.
   * @returns Whatever `task` resolves to.
   */
  runExclusive<T>(task: () => Promise<T>): Promise<T> {
    const run = this.tail.then(task, task);
    // Keep the chain alive after a rejected task, and don't leak an unhandled rejection.
    this.tail = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  /**
   * Emit `cart:changed` without performing a network request.
   *
   * For callers that detect drift between their local view and the server cart
   * (e.g. a stale line item key) and need dependent UI to re-render from the
   * authoritative cart.
   * @param cart The authoritative cart to broadcast.
   * @param reason Why the cart changed.
   */
  notifyChanged(
    cart: Cart,
    reason: 'add' | 'change' | 'update' | 'clear' | 'get' = 'change',
  ): void {
    this.bus.emit(CartEvents.Changed, { reason, cart });
  }

  /**
   * Fetch the current cart (`GET /cart.js`).
   *
   * Emits: `cart:request`, `cart:response` | `cart:error`.
   * @returns The current `Cart`.
   */
  async get(): Promise<Cart> {
    this.logger.info('Getting cart');
    const endpoint = cartRoute('cart.js');
    this.bus.emit(CartEvents.Request, { endpoint, method: 'GET' });
    try {
      const res = await fetch(endpoint, {
        headers: { Accept: 'application/json', 'X-Source': 'mini-cart' },
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`Cart get failed: ${res.status}`);
      const cart = await res.json();
      this.bus.emit(CartEvents.Response, { endpoint, method: 'GET', cart });
      return cart;
    } catch (error) {
      this.bus.emit(CartEvents.Error, { endpoint, method: 'GET', error });
      throw error;
    }
  }

  /**
   * Add one or more items to the cart (`POST /cart/add.js`).
   *
   * Emits: `cart:request`, `cart:add:start`, `cart:response`, `cart:add:success`, `cart:changed` | `cart:error`, `cart:add:error`.
   * @param payload Items to add (supports `id` or `variantId`).
   * @returns Updated `Cart` after the add completes.
   */
  async add(payload: AddToCartPayload): Promise<Cart> {
    this.logger.info('Adding to cart', payload);
    const endpoint = cartRoute('cart/add.js');
    this.bus.emit(CartEvents.Request, { endpoint, method: 'POST', payload });
    this.bus.emit(CartEvents.AddStart, payload);
    const normalized = {
      items: payload.items.map((i) => ({
        id: (i as any).id ?? (i as any).variantId,
        quantity: i.quantity ?? 1,
        properties: withCartSource(i.properties, 'mini-cart:cart-service'),
      })),
    } as any;
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: jsonHeaders,
        credentials: 'same-origin',
        body: JSON.stringify(normalized),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message);
      }
      const cart = await this.get();
      this.bus.emit(CartEvents.Response, { endpoint, method: 'POST', cart });
      this.bus.emit(CartEvents.AddSuccess, { payload, cart });
      this.bus.emit(CartEvents.Changed, { reason: 'add', cart });
      return cart;
    } catch (error) {
      this.bus.emit(CartEvents.Error, { endpoint, method: 'POST', error });
      this.bus.emit(CartEvents.AddError, { payload, error });
      throw error;
    }
  }

  /**
   * Change a line's quantity or properties (`POST /cart/change.js`).
   *
   * Emits: `cart:request`, `cart:change:start`, `cart:response`, `cart:change:success`, `cart:changed` | `cart:error`, `cart:change:error`.
   * @param payload Change descriptor; provide `id` (line key) or `line` (1-indexed).
   * @returns Updated `Cart` after the change completes.
   */
  async change(payload: ChangeLinePayload): Promise<Cart> {
    this.logger.info('Changing cart', payload);
    const endpoint = cartRoute('cart/change.js');
    this.bus.emit(CartEvents.Request, { endpoint, method: 'POST', payload });
    this.bus.emit(CartEvents.ChangeStart, payload);
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: jsonHeaders,
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message);
      }
      // change.js already returns the full cart — a follow-up GET /cart.js only
      // widens the window for a concurrent write and burns Ajax rate limit.
      const cart = (await res.json()) as Cart;
      this.bus.emit(CartEvents.Response, { endpoint, method: 'POST', cart });
      this.bus.emit(CartEvents.ChangeSuccess, { payload, cart });
      this.bus.emit(CartEvents.Changed, { reason: 'change', cart });
      return cart;
    } catch (error) {
      this.bus.emit(CartEvents.Error, { endpoint, method: 'POST', error });
      this.bus.emit(CartEvents.ChangeError, { payload, error });
      throw error;
    }
  }

  /**
   * Update cart note/attributes (`POST /cart/update.js`).
   *
   * Emits: `cart:request`, `cart:update:start`, `cart:response`, `cart:update:success`, `cart:changed` | `cart:error`, `cart:update:error`.
   * @param payload Fields to update.
   * @returns Updated `Cart` after the update completes.
   */
  async update(payload: UpdateCartPayload): Promise<Cart> {
    this.logger.info('Updating cart', payload);
    const endpoint = cartRoute('cart/update.js');
    this.bus.emit(CartEvents.Request, { endpoint, method: 'POST', payload });
    this.bus.emit(CartEvents.UpdateStart, payload);
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: jsonHeaders,
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message);
      }
      const cart = (await res.json()) as Cart;
      this.bus.emit(CartEvents.Response, { endpoint, method: 'POST', cart });
      this.bus.emit(CartEvents.UpdateSuccess, { payload, cart });
      this.bus.emit(CartEvents.Changed, { reason: 'update', cart });
      return cart;
    } catch (error) {
      this.bus.emit(CartEvents.Error, { endpoint, method: 'POST', error });
      this.bus.emit(CartEvents.UpdateError, { payload, error });
      throw error;
    }
  }

  /**
   * Clear the cart (`POST /cart/clear.js`).
   *
   * Emits: `cart:request`, `cart:clear:start`, `cart:response`, `cart:changed`, `cart:clear:success` | `cart:error`, `cart:clear:error`.
   * @returns Updated `Cart` after clearing (usually empty).
   */
  async clear(): Promise<Cart> {
    this.logger.info('Clearing cart');
    const endpoint = cartRoute('cart/clear.js');
    this.bus.emit(CartEvents.Request, { endpoint, method: 'POST' });

    this.bus.emit(CartEvents.ClearStart, undefined);
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: jsonHeaders,
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`Clear cart failed: ${res.status}`);
      const cart = (await res.json()) as Cart;
      this.bus.emit(CartEvents.Response, { endpoint, method: 'POST', cart });
      this.bus.emit(CartEvents.Changed, { reason: 'clear', cart });
      this.bus.emit(CartEvents.ClearSuccess, { cart });
      return cart;
    } catch (error) {
      this.bus.emit(CartEvents.Error, { endpoint, method: 'POST', error });
      this.bus.emit(CartEvents.ClearError, { error });
      throw error;
    }
  }
}
